import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import type { BillStatus } from '../../../types/bills';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

/** Grower Products persist as the `WorkOrder` model. */
export type GrowerProductStatus = $Enums.WorkOrderStatus;

/**
 * What `submit` (Draft → Submitted) checks, in the server's order after the grower. A fact left
 * undefined is not known and never denies — the server leaves the shipment facts out when the
 * order has no shipment, so its own not-found answer still comes first.
 */
export type GrowerProductSubmissionFacts = {
  productType?: $Enums.GrowerProductType | null;
  hasCommissionRate?: boolean;
  hasDestination?: boolean;
  hasDeliveryDate?: boolean;
  destinationIsWarehouse?: boolean;
  lineItemCount?: number;
  everyLineHasUnitsOrdered?: boolean;
};

/** What `mark as received` (Receiving → Pending Settlement) checks, in the server's order. */
export type GrowerProductReceiptFacts = {
  /** The first line missing its received quantity (`growerProductMissingReceipt`). */
  missingReceipt?: GrowerProductMissingReceipt;
  hasDeliveryDate?: boolean;
  hasHarvestDate?: boolean;
};

/** What `close` (Pending Settlement → Closed) checks for a consignment order, in the server's order. */
export type GrowerProductClosureFacts = {
  productType?: $Enums.GrowerProductType | null;
  /** Lots received on this order, and their repacked children, that still hold quantity. */
  lotsWithQuantity?: number;
  /**
   * The sales-order lines sold from those lots without a consignment payable are exactly matched by
   * settled consignment bill lines (the server compares the two counts for equality).
   */
  salesOrderLinesSettled?: boolean;
  /** Consignment bills attached to this order not yet final (`growerProductSettlementIsFinal`). */
  outstandingSettlements?: number;
};

export type GrowerProductFacts = {
  /** undefined = not known (a status-predicated write that missed): nothing status-gated opens. */
  status: GrowerProductStatus | undefined;
  hasGrower: boolean;
  numOpenSettlements: number;
  /**
   * The operation facts below are read only by the door that asks the operation; every one of
   * them reads undefined as "not known", which never denies.
   */
  /** delete: this order is its organization's default grower product template. */
  isDefaultTemplate?: boolean;
  submission?: GrowerProductSubmissionFacts;
  receipt?: GrowerProductReceiptFacts;
  closure?: GrowerProductClosureFacts;
  /** revert out of PENDING_SETTLEMENT: a bill attached to this order is paid or finalized. */
  hasPaidBill?: boolean;
  /** revert out of PENDING_SETTLEMENT: a lot this order received is in use. */
  hasActiveLots?: boolean;
  /** revert out of RECEIVING: a lot this order received has been used (`AllowMidReceivingShipping` only). */
  lotsUsed?: boolean;
};

export type GrowerProductMissingReceipt = 'weight' | 'units';

/**
 * The receipt line check, shared so the server and any surface name the SAME first line: a line
 * bought by weight needs its weight received, any other line its units received.
 */
export const growerProductMissingReceipt = (
  lines: readonly { enteredByWeight: boolean | null; weightReceived: number | null; unitsReceived: number | null }[]
): GrowerProductMissingReceipt | null => {
  for (const line of lines) {
    if (line.enteredByWeight && line.weightReceived === null) return 'weight';
    if (!line.enteredByWeight && line.unitsReceived === null) return 'units';
  }
  return null;
};

/** The consignment bill statuses that no longer hold a grower product open. */
export const GROWER_PRODUCT_FINAL_SETTLEMENT_STATUSES: readonly BillStatus[] = [
  'FINALIZED',
  'PAID_FULL',
  'VOIDED',
  'CANCELLED',
];

export const growerProductSettlementIsFinal = (status: BillStatus): boolean =>
  GROWER_PRODUCT_FINAL_SETTLEMENT_STATUSES.includes(status);

/** The attached-bill statuses that do not count as an active (open) settlement. */
export const GROWER_PRODUCT_INACTIVE_SETTLEMENT_STATUSES: readonly BillStatus[] = ['DRAFT', 'VOIDED', 'CANCELLED'];

/** `numOpenSettlements` counts the attached bills this answers true for — on the api and the web. */
export const growerProductSettlementIsActive = (status: BillStatus): boolean =>
  !GROWER_PRODUCT_INACTIVE_SETTLEMENT_STATUSES.includes(status);

export const GROWER_PRODUCT_FIELDS = [
  'orderDate',
  'grower',
  'growerContact',
  'fieldTicketId',
  'harvestDate',
  'harvestLocation',
  'commissionRate',
  'productType',
  'settlementBasis',
  'destination',
  'deliveryDate',
  'lineItems',
  'expenses',
  'label',
] as const;

export type GrowerProductField = (typeof GROWER_PRODUCT_FIELDS)[number];

/**
 * The lifecycle operations the policy explains (op ids are the machine's events, spaced). Whether
 * a status accepts `submit`, `mark as received`, `close` or `revert` at all stays with the state
 * machine (`api/src/services/growerProducts/growerProduct.states.ts`); the policy judges their facts.
 */
export const GROWER_PRODUCT_OPS = ['submit', 'mark as received', 'close', 'revert', 'cancel'] as const;

export type GrowerProductOp = (typeof GROWER_PRODUCT_OPS)[number];

const TO_SUBMITTED: readonly GrowerProductStatus[] = ['DRAFT', 'SUBMITTED'];
const TO_RECEIVING: readonly GrowerProductStatus[] = [...TO_SUBMITTED, 'RECEIVING'];

export const GROWER_PRODUCT_LINE_ITEMS_EDITABLE_STATUSES: readonly GrowerProductStatus[] = TO_RECEIVING;
const TO_PENDING_SETTLEMENT: readonly GrowerProductStatus[] = [...TO_RECEIVING, 'PENDING_SETTLEMENT'];
const TO_CLOSED: readonly GrowerProductStatus[] = [...TO_PENDING_SETTLEMENT, 'CLOSED'];

export const GROWER_PRODUCT_EXPENSES_EDITABLE_STATUSES: readonly GrowerProductStatus[] = TO_CLOSED;

/** The statuses the state machine accepts `cancel` from. */
export const GROWER_PRODUCT_CANCELLABLE_STATUSES: readonly GrowerProductStatus[] = TO_SUBMITTED;

const inWindow = (window: readonly GrowerProductStatus[], status: GrowerProductStatus | undefined): boolean =>
  status !== undefined && window.includes(status);

const statusAllowsField = (status: GrowerProductStatus | undefined, field: GrowerProductField): boolean => {
  switch (field) {
    case 'label':
      return status === 'DRAFT';
    case 'grower':
      return inWindow(TO_SUBMITTED, status);
    case 'destination':
    case 'lineItems':
      return inWindow(TO_RECEIVING, status);
    case 'growerContact':
    case 'fieldTicketId':
    case 'harvestDate':
    case 'harvestLocation':
    case 'deliveryDate':
      return inWindow(TO_PENDING_SETTLEMENT, status);
    case 'expenses':
      return inWindow(TO_CLOSED, status);
    case 'orderDate':
    case 'commissionRate':
    case 'productType':
    case 'settlementBasis':
      return true;
  }
};

const GROWER_REQUIRED_FIELDS: readonly GrowerProductField[] = [
  'growerContact',
  'fieldTicketId',
  'harvestDate',
  'harvestLocation',
];

/** The fields an open settlement locks (`GROWER_PRODUCT_SETTLEMENT_OPEN`). */
export const GROWER_PRODUCT_SETTLEMENT_LOCKED_FIELDS: readonly GrowerProductField[] = [
  'lineItems',
  'commissionRate',
  'productType',
  'settlementBasis',
  'expenses',
];

const decideField = (facts: GrowerProductFacts, field: GrowerProductField): Decision => {
  if (field === 'label') {
    return statusAllowsField(facts.status, field)
      ? allow()
      : deny('GROWER_PRODUCT_LABEL_LOCKED', 'The label can only be changed in Draft.');
  }

  if (!statusAllowsField(facts.status, field)) {
    return deny(
      'GROWER_PRODUCT_FIELD_LOCKED',
      facts.status === undefined
        ? 'This field is locked while the grower product status is unavailable.'
        : `This field is locked once the grower product is ${getStatusLabel(facts.status)}.`
    );
  }

  if (!facts.hasGrower && GROWER_REQUIRED_FIELDS.includes(field)) {
    return deny('GROWER_PRODUCT_GROWER_REQUIRED', 'Select a grower first.');
  }

  if (facts.numOpenSettlements !== 0 && GROWER_PRODUCT_SETTLEMENT_LOCKED_FIELDS.includes(field)) {
    return deny('GROWER_PRODUCT_SETTLEMENT_OPEN', 'Locked while a settlement is open.');
  }

  return allow();
};

/** Delete guards in the server's order: default template → Draft only. */
const decideDelete = (facts: GrowerProductFacts): Decision => {
  if (facts.isDefaultTemplate === true) {
    return deny('GROWER_PRODUCT_DELETE_LOCKED', "The default template can't be deleted.");
  }

  return facts.status === 'DRAFT'
    ? allow()
    : deny(
        'GROWER_PRODUCT_DELETE_LOCKED',
        facts.status === undefined
          ? 'Only Draft grower products can be deleted.'
          : `Only Draft grower products can be deleted. This one is ${getStatusLabel(facts.status)}.`
      );
};

const submitIncomplete = (message: string): Decision => deny('GROWER_PRODUCT_SUBMIT_INCOMPLETE', message);

const decideSubmit = (facts: GrowerProductFacts): Decision => {
  const submission = facts.submission;
  if (submission === undefined) return allow();

  if (!facts.hasGrower) return submitIncomplete('Add a Grower to submit.');
  if (submission.productType === null) return submitIncomplete('Add a Product Type to submit.');
  if (submission.productType === 'CONSIGNMENT' && submission.hasCommissionRate === false) {
    return submitIncomplete('Add a Commission Rate to submit.');
  }
  if (submission.hasDestination === false) return submitIncomplete('Add a Destination to submit.');
  if (submission.hasDeliveryDate === false) {
    return submitIncomplete('Set the delivery date to submit.');
  }
  if (submission.destinationIsWarehouse === false) {
    return submitIncomplete('The destination must be a warehouse.');
  }
  if (submission.lineItemCount === 0) return submitIncomplete('Add at least one product to submit.');
  if (submission.everyLineHasUnitsOrdered === false) {
    return submitIncomplete('Add units ordered for every product.');
  }

  return allow();
};

const decideReceipt = (facts: GrowerProductFacts): Decision => {
  const receipt = facts.receipt;
  if (receipt === undefined) return allow();

  if (receipt.missingReceipt === 'weight') {
    return deny('GROWER_PRODUCT_RECEIPT_INCOMPLETE', 'Add weight received for products bought by weight.');
  }
  if (receipt.missingReceipt === 'units') {
    return deny('GROWER_PRODUCT_RECEIPT_INCOMPLETE', 'Add units received for products bought by unit.');
  }
  if (receipt.hasDeliveryDate === false) {
    return deny('GROWER_PRODUCT_RECEIPT_INCOMPLETE', 'Set the delivery date to receive.');
  }
  if (receipt.hasHarvestDate === false) {
    return deny('GROWER_PRODUCT_RECEIPT_INCOMPLETE', 'Set the harvest date to receive.');
  }

  return allow();
};

/** An own-product order closes with no lot or settlement check, exactly as the server returns early. */
const decideClose = (facts: GrowerProductFacts): Decision => {
  const closure = facts.closure;
  if (closure === undefined || closure.productType === 'OWN_PRODUCT') return allow();

  if (closure.lotsWithQuantity !== undefined && closure.lotsWithQuantity > 0) {
    return deny('GROWER_PRODUCT_CLOSE_LOCKED', 'Lots from this grower product still have quantity.');
  }
  if (closure.salesOrderLinesSettled === false) {
    return deny('GROWER_PRODUCT_CLOSE_LOCKED', 'Sales orders that sold this product are still unsettled.');
  }
  if (closure.outstandingSettlements !== undefined && closure.outstandingSettlements > 0) {
    return deny('GROWER_PRODUCT_CLOSE_LOCKED', 'This grower product has open settlements.');
  }

  return allow();
};

/** `revert` is judged on the edge the status implies; each edge has its own facts and sentences. */
const decideRevert = (facts: GrowerProductFacts): Decision => {
  switch (facts.status) {
    case 'RECEIVING':
      return facts.lotsUsed === true
        ? deny('GROWER_PRODUCT_REVERT_LOCKED', "Can't revert: lots from this order are already in use.")
        : allow();
    case 'PENDING_SETTLEMENT':
      if (facts.hasPaidBill === true) {
        return deny('GROWER_PRODUCT_REVERT_LOCKED', "Can't revert: a bill on this grower product is paid.");
      }
      return facts.hasActiveLots === true
        ? deny('GROWER_PRODUCT_REVERT_LOCKED', "Can't revert: lots from this order are already in use.")
        : allow();
    default:
      return allow();
  }
};

/** UI action names used by the header and its confirmation controls. */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  submit: 'Submit',
  receive: 'Receive',
  mark_as_received: 'Confirm Receipt',
  mark_as_shipped: 'Confirm Shipped',
  close: 'Close',
  revert: 'Revert Grower Product',
  cancel: 'Cancel Grower Product',
  uncancel: 'Uncancel Grower Product',
};

export const growerProductEventUnavailableMessage = (
  status: GrowerProductStatus | undefined,
  event: string
): string => {
  const action = EVENT_LABELS[event] ?? 'This action';
  return status === undefined
    ? `${action} isn't available while the grower product status is unavailable.`
    : `${action} isn't available while the grower product is ${getStatusLabel(status)}.`;
};

/** The state machine owns the cancellable window; the denial uses its UI action label. */
const decideCancel = (facts: GrowerProductFacts): Decision => {
  if (inWindow(GROWER_PRODUCT_CANCELLABLE_STATUSES, facts.status)) return allow();

  return deny('GROWER_PRODUCT_CANCEL_LOCKED', growerProductEventUnavailableMessage(facts.status, 'cancel'));
};

const decideOperation = (facts: GrowerProductFacts, op: GrowerProductOp): Decision => {
  switch (op) {
    case 'submit':
      return decideSubmit(facts);
    case 'mark as received':
      return decideReceipt(facts);
    case 'close':
      return decideClose(facts);
    case 'revert':
      return decideRevert(facts);
    case 'cancel':
      return decideCancel(facts);
  }
};

const fieldRule = (facts: GrowerProductFacts, action: Action<GrowerProductField, GrowerProductOp>): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  orderDate: fieldRule,
  grower: fieldRule,
  growerContact: fieldRule,
  fieldTicketId: fieldRule,
  harvestDate: fieldRule,
  harvestLocation: fieldRule,
  commissionRate: fieldRule,
  productType: fieldRule,
  settlementBasis: fieldRule,
  destination: fieldRule,
  deliveryDate: fieldRule,
  lineItems: fieldRule,
  expenses: fieldRule,
  label: fieldRule,
} satisfies Record<GrowerProductField, typeof fieldRule>;

export const growerProductPolicy: Policy<GrowerProductFacts, GrowerProductField, GrowerProductOp> = definePolicy<
  GrowerProductFacts,
  GrowerProductField,
  GrowerProductOp
>({
  kind: 'grower product',
  fields: GROWER_PRODUCT_FIELDS,
  ops: GROWER_PRODUCT_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type === 'delete') return decideDelete(facts);
    if (action.type === 'perform') return decideOperation(facts, action.op);
    return allow();
  },
  field: fieldRules,
});
